<template>
  <div ref="containerRef"
       class="select"
       :class="{ disabled }"
  >
    <!-- Server-side selection state (select payloads) -->
    <input v-if="request.select"
           v-for="value in formattedSelection"
           :key="value"
           type="hidden"
           :disabled="disabled"
           :name="`${SELECT_KEY}${parsedName}`"
           :value="value"
    />

    <!-- Selected values -->
    <input v-for="value in inputValues"
           :key="value"
           type="hidden"
           :disabled="disabled"
           :name="parsedName"
           :value="value"
    />

    <SelectDropdown
        v-if="!inline"
        ref="selectRef"
        :input-values="temporaryValues"
        :input-class="inputClass"
        :small="small"
        :input-group-class="inputGroupClass"
        :disable-search="disableSearch"
        :disabled="disabled"
        :loading="loading"
        :preloading="preloading"
        :clearable="clearable"
        :multiple="multiple"
        :request="request"
        :placeholder="parsedPlaceholder"
        :popper-class="popperClass"
        :container="containerRef"
        :options-props="optionsData"
        :all-selected="allSelected"
        :all-options="flatOptionsList"
        :title-key="titleKey"
        :value-key="valueKey"
        :initial-load="initialLoad"
        :is-static="isStatic"
        :confirmable="confirmable"
        :autocomplete="autocomplete"
        :quick-filter="quickFilter"
        :placement="placement"
        :error-message="requestError"
        @clear="clearValues"
        @undo="undo"
        @all="selectAll"
        @apply="apply"
        @load="load"
        @remove="onRemove"
        @select="onSelect"
        @select-group="onSelectGroup"
        @group-toggle="onGroupToggle"
        @scroll-end="onScrollEnd"
        @page-change="onPageChange"
        @confirm="emit('confirm')"
        @focus="emit('focus')"
        @blur="emit('blur')"
    >
      <template #option="data">
        <slot name="option" v-bind="data" />
      </template>
      <template #emptyList="data">
        <slot name="emptyList" v-bind="data" />
      </template>
      <template #search="data">
        <slot name="search" v-bind="data" />
      </template>
      <template #footer>
        <slot name="footer" />
      </template>
    </SelectDropdown>

    <InlineSelect
        v-else
        ref="selectRef"
        :disable-search="disableSearch"
        :disable-controls="disableControls"
        :request="request"
        :multiple="multiple"
        :is-static="isStatic"
        :options-props="optionsData"
        @all="selectAll"
        @apply="apply"
        @select="onSelect"
        @select-group="onSelectGroup"
        @group-toggle="onGroupToggle"
        @scroll-end="onScrollEnd"
        @page-change="onPageChange"
    >
      <template #option="data">
        <slot name="option" v-bind="data" />
      </template>
      <template #emptyList="data">
        <slot name="emptyList" v-bind="data" />
      </template>
    </InlineSelect>
  </div>
</template>

<script setup>
import FormControlProps from "@/js/Elements/Form/FormControlProps.js";
import SelectDropdown from "@/js/Elements/Select/SelectDropdown.vue";
import InlineSelect from "@/js/Elements/Select/InlineSelect.vue";
import {computed, inject, nextTick, reactive, ref, watch} from "vue";
import {useFormControlParser} from "@/js/Elements/Form/FormControl.js";
import {useI18n} from "vue-i18n";
import {useStore} from "vuex";
import {escapeRegex} from "@/js/helpers.js";

// Selection map keys (select payloads track selection server-side)
const PRELOAD_KEY = 'preload';
const SELECT_KEY = 'selected_';
const SELECT_GROUP_KEY = 'group_id';
const SELECT_GROUP_WITH_SEARCH_KEY = 's_group_id';
const SELECT_ITEM_KEY = 'id';
const SELECT_SEARCH_KEY = 's';

const { t } = useI18n();
const store = useStore();
const formData = inject('form:data', {});

const emit = defineEmits(['update:modelValue', 'select', 'confirm', 'focus', 'blur']);
const props = defineProps({
  ...FormControlProps,
  inputClass: [String, Object, Array],
  optionsClass: [String, Object, Array],
  showGoToPage: Boolean,
  showPerPage: Boolean,
  quickFilter: Boolean,
  popperClass: [String, Object, Array],
  filters: Array,
  searchFilter: {
    type: Function,
    default: (option, search, key) => {
      if (!search) return true;
      search = new RegExp(escapeRegex(search.toString().toLowerCase()));
      return option[key].toString().toLowerCase().match(search);
    }
  }
});

defineExpose({
  reload: refresh,
  focus: () => selectRef.value?.open(),
  clear: () => clearValues(true),
});

const {parsedPlaceholder, parsedName} = useFormControlParser(props, {
  defaultPlaceholder: t('validation.select_placeholder'),
});
const selectFieldName = computed(() => parsedName.value.replace('[]', ''));

// --- State ---------------------------------------------------------------
const containerRef = ref();
const selectRef = ref();

const options = reactive([]);          // loaded options (may be grouped)
const preloadedOptions = reactive([]); // selected options kept for display across pages
const temporaryValues = reactive([]);  // working selection (staged until applied)
const inputValues = reactive([]);      // committed selection (drives hidden inputs + model)

const request = reactive({
  url: typeof props.payload === 'string' ? props.payload : null,
  search: null,
  page: 1,
  nextPage: 1,
  lastPage: null,
  perPage: null,
  showPerPage: [],
  paginated: false,
  select: false,
  preSelection: null,
  selection: {}
});

const isGrouped = ref(false);
const isStatic = ref(false);
const loading = ref(false);
const preloading = ref(false);
const initialLoad = ref(false);
const allSelected = ref(0);
const requestError = ref();

const watchers = {};
let ignoreValueWatcher = false;
let searchTimer = null;

// --- Computed ------------------------------------------------------------
// Plain reactive payload handed down to the options list. No cloning, no
// callbacks: option events come back as explicit emits instead.
const optionsData = computed(() => ({
  options,
  request,
  loading: loading.value,
  initialLoad: initialLoad.value,
  isGrouped: isGrouped.value,
  showPagination: initialLoad.value && !isStatic.value && (props.inline || isGrouped.value),
  multiple: props.multiple,
  inline: props.inline,
  disabled: props.disabled,
  grid: props.grid,
  autocomplete: props.autocomplete,
  valueKey: props.valueKey,
  titleKey: props.titleKey,
  groupValueKey: props.groupValueKey,
  groupTitleKey: props.groupTitleKey,
  optionsClass: props.optionsClass,
  showGoToPage: props.showGoToPage,
  showPerPage: props.showPerPage,
}));

const flatOptionsList = computed(() => {
  const list = options.length && typeof options[0]?.items !== 'undefined'
      ? options.flatMap(option => option.items)
      : options;
  return [...list, ...preloadedOptions];
});

const formattedSelection = computed(() => Object.values(request.selection).map((value) =>
    [value.key, value.selected === 1, value.value === null ? '' : value.value].join(';')
    + (value.additional ? `|${value.additional}` : '')
));

const shouldPreload = computed(() => {
  if (request.paginated && !temporaryValues.length) return false;
  return !props.inline && (request.select || request.paginated);
});

// --- Init ----------------------------------------------------------------
init();

function init() {
  applyModelValue();
  storeValues(true);
  setupDependencyWatcher();

  if (props.autocomplete) {
    setupWatchers();
    return;
  }

  if (typeof props.payload !== 'string') {
    isStatic.value = true;
    initialLoad.value = true;
    addOptions(props.payload);
    return;
  }

  parsePayload();

  if (request.select && inputValues.length) {
    inputValues.forEach(value => addToSelection({ [props.valueKey]: value }, true, SELECT_ITEM_KEY));
  }

  if (shouldPreload.value) {
    preload();
  } else {
    preloading.value = true;
    load();
  }
}

function parsePayload() {
  request.paginated = /payload\/paginated\//.test(props.payload);
  request.select = /payload\/select\//.test(props.payload);
}

// --- Loading -------------------------------------------------------------
function load(force = false) {
  if (requestError.value) return;

  if (!force && (loading.value || isStatic.value || (request.lastPage !== null && request.page > request.lastPage))) {
    return;
  }

  loading.value = true;
  unwatch(force);

  const params = {
    page: request.nextPage,
    per_page: request.perPage,
    [props.searchKey]: request.search,
  };

  if (props.filters) {
    params['selected_filters'] = props.filters;
  }

  if (request.select) {
      params[`${SELECT_KEY}${selectFieldName.value}`] = formattedSelection.value;
  }

  return sendRequest(params).then(({ data, pagination }) => {
    if (pagination) {
      request.paginated = true;
      request.page = pagination.current_page;
      request.url = pagination.next_page_url;
      request.lastPage = pagination.last_page;
      request.nextPage = Math.min(request.page + 1, pagination.last_page);
      request.perPage = pagination.per_page;
      request.showPerPage = pagination.show_per_page || [];
    } else if (!request.select) {
      isStatic.value = !props.autocomplete;
    }

    addOptions(data);
    initialLoad.value = true;
  }).finally(() => {
    preloading.value = false;
    loading.value = false;
  });
}

function freshLoad(force = false) {
  initialLoad.value = false;
  loading.value = false;
  request.lastPage = null;
  request.page = 1;
  request.nextPage = 1;
  options.splice(0);
  return load(force);
}

function refresh() {
  if (!request.paginated) {
    return freshLoad(true);
  }

  preloadedOptions.splice(0);
  return Promise.resolve(freshLoad(true))
      .then(() => (shouldPreload.value ? preload() : null))
      .then(() => pruneSelectionToLoaded());
}

function preload() {
  if (preloading.value) return;
  preloading.value = true;

  const params = {};

  if (request.select) {
    // Only user-action deltas are sent; the server holds the stored selection
    // and returns it via preSelection. Initial selection is never echoed back.
    params[`${SELECT_KEY}${selectFieldName.value}`] = formattedSelection.value;
    params[PRELOAD_KEY] = 1;
  } else if (request.paginated) {
    params[PRELOAD_KEY] = temporaryValues;
  }

  return sendRequest(params).then(({ data, preSelection }) => {
    request.preSelection = preSelection;
    (request.select ? preSelection.items : data).forEach(option => preloadedOptions.push(option));

    if (!request.select) return;

    if (preSelection.items?.length) {
      preSelection.items.forEach(item => addToValues(item[props.valueKey], true));
      if (!initialLoad.value) emitSelect();
    } else {
      clearValues();
    }
  }).finally(() => {
    preloading.value = false;
  });
}

function sendRequest(params = {}) {
  if (requestError.value) return Promise.reject();

  return store.dispatch('payload/load', {
    url: props.payload,
    params: { ...(props.params || {}), ...params },
    cache: false,
    fullResponse: true
  }).then((response) => {
    let data = response?.data?.data?.data || response?.data?.data;
    let pagination = response?.data?.data;
    const preSelection = response?.data?.data?.selection;

    if (Object.prototype.toString.call(data) === "[object Object]") {
      data = Object.values(data);
    }
    if (Object.prototype.toString.call(pagination) !== "[object Object]") {
      pagination = null;
    }

    return {
      pagination,
      data: Array.isArray(data) ? data : [],
      preSelection,
    };
  }).catch((error) => {
    requestError.value = error?.response?.data?.message || error?.message;
    throw error;
  });
}

// --- Building options ----------------------------------------------------
function addOptions(list) {
  list = JSON.parse(JSON.stringify(list || []));
  (Array.isArray(list) ? list : Object.values(list)).forEach((raw) => {
    const isGroup = typeof raw?.items !== 'undefined';
    const option = mapOption(raw, isGroup);

    if (isGroup) {
      isGrouped.value = true;
      option.expanded = true;
      option.items = option.items.map(item => mapOption(item));
    }

    options.push(option);
  });

  // Sync selection after each refresh of the options list
  if (request.select) {
    applySelectionToOptions();
    parseAllSelectedFromSelection();
    storeValues(true);
    seedSelectionFromModel();
  } else if (isStatic.value) {
    clearStaticPayloadValues();
  }

  // In select mode the server response is the source of truth. Suppress the
  // immediate modelValue watcher (re)registered below so it can't reset the
  // selection we just loaded when the bound model is empty.
  if (request.select) ignoreValueWatcher = true;

  if (!props.inline || isStatic.value) {
    setupWatchers();
  } else {
    nextTick(setupWatchers);
  }

  if (request.select) nextTick(() => { ignoreValueWatcher = false; });
}

function mapOption(option, isGroup = false) {
  if (request.select || typeof option._selected !== 'undefined') {
    if (typeof option.selected === 'undefined') option.selected = option._selected;
    if (!option.selected) allSelected.value = 0;
    if (!isGroup) addToValues(option[props.valueKey].toString(), option.selected);
  }

  option.selected = option.selected ? 1 : 0;
  return setOptionVisibility(option);
}

function setOptionVisibility(option) {
  if (typeof option.items !== 'undefined') {
    option.items = option.items.map(setOptionVisibility);
    option._visible_ = option.items.some(item => item._visible_);
  } else {
    option._visible_ = request.search && isStatic.value
        ? !!props.searchFilter(option, request.search, props.titleKey)
        : true;
  }
  return option;
}

// --- Selection actions ---------------------------------------------------
function onSelect(option) {
  if (option.selected && !props.multiple && !props.clearable) return;

  const selected = option.selected ? 0 : 1;
  const value = option[props.valueKey];
  option.selected = selected;

  if (request.paginated && selected) {
    const exists = preloadedOptions.some(o => o[props.valueKey].toString() === value.toString());
    if (!exists) preloadedOptions.push(option);
  }

  addToSelection(option, selected, SELECT_ITEM_KEY);
  addToValues(value, selected);
  storeValues();
  parseAllSelectedFromSelection();

  if (!props.multiple) selectRef.value?.close?.();
}

function onRemove(value) {
  if (value === undefined || value === null) return;

  addToValues(value, 0);
  storeValues(true);
  selectOptions();

  const index = preloadedOptions.findIndex(o => o[props.valueKey].toString() === value);
  if (index !== -1) preloadedOptions.splice(index, 1);
}

function onSelectGroup(group) {
  group.items.forEach((option) => {
    addToValues(option[props.valueKey], group.selected);
    option.selected = group.selected;
    delete request.selection[`${option[props.valueKey]}-${SELECT_ITEM_KEY}`];
  });

  addToSelection(group, group.selected, request.search ? SELECT_GROUP_WITH_SEARCH_KEY : SELECT_GROUP_KEY);
  storeValues();
}

function onGroupToggle(group) {
  const target = options.find(g => g[props.groupValueKey] === group[props.groupValueKey]);
  if (target) target.expanded = target.expanded ? 0 : 1;
}

function onScrollEnd() {
  if ((!props.inline && isGrouped.value) || props.autocomplete) return;
  if (request.page === request.lastPage) return;
  load();
}

function onPageChange(value) {
  initialLoad.value = false;
  options.splice(0);
  request.nextPage = value;
  load();
}

function selectAll(selected) {
  if (request.select) {
    if (!request.search) {
      Object.keys(request.selection).forEach(key => delete request.selection[key]);
    }
    request.selection[`${request.search}-${SELECT_SEARCH_KEY}`] = {
      key: SELECT_SEARCH_KEY,
      value: request.search,
      search_key: props.titleKey,
      selected
    };
  }

  allSelected.value = selected;
  flatOptionsList.value.forEach(option => addToValues(option[props.valueKey].toString(), selected));
  selectOptions();
  storeValues();
}

function undo() {
  allSelected.value = 0;
  temporaryValues.splice(0);
  selectOptions(inputValues);

  preloadedOptions.forEach((option, index) => {
    const kept = inputValues.includes(option[props.valueKey].toString());
    if (!kept) preloadedOptions.splice(0, index);
  });
}

function clearValues(force = false) {
  allSelected.value = 0;
  temporaryValues.splice(0);
  selectOptions();
  Object.keys(request.selection).forEach(key => delete request.selection[key]);
  storeValues(force);
}

// --- Server-side selection map -------------------------------------------
function addToSelection(option, selected, key) {
  if (!request.select) return;

  if (Object.prototype.toString.call(option) !== "[object Object]") {
    option = { [props.valueKey]: option, [props.groupKey]: null };
  }

  const value = option[props.valueKey];
  const groupId = key === SELECT_ITEM_KEY ? option[props.groupKey] : null;
  const additional = key === SELECT_GROUP_WITH_SEARCH_KEY ? request.search : null;
  const selectionKey = `${value}-${key}`;

  // Single select keeps at most one entry
  if (key === SELECT_ITEM_KEY && !props.multiple) {
    Object.keys(request.selection).forEach((k) => {
      if (k === selectionKey && selected) return;
      delete request.selection[k];
    });
  }

  request.selection[selectionKey] = {
    key,
    value,
    selected: selected ? 1 : 0,
    additional,
    groupId
  };
}

function seedSelectionFromModel() {
  if (!request.select) return;

  (Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue])
      .filter(value => value !== undefined && value !== null && value !== '')
      .forEach((value) => {
        if (request.selection[`${value}-${SELECT_ITEM_KEY}`]) return;
        addToSelection({ [props.valueKey]: value }, true, SELECT_ITEM_KEY);
      });
}

function parseAllSelectedFromSelection() {
  if (!request.select) return;

  const entries = Object.values(request.selection);
  const allChecked = !entries.find(s => !s.selected)
      && !!entries.find(s => !s.value || s.value === request.search);
  allSelected.value = allChecked ? 1 : 0;
}

// Freshly loaded options are marked from the server response. Reconcile them
// with explicit per-item selections/deselections so checkmarks match the
// selection that the output already reflects. Options not tracked individually
// (e.g. "select all" by search) keep whatever the server returned.
function applySelectionToOptions() {
  if (!request.select) return;

  flatOptionsList.value.forEach((option) => {
    const entry = request.selection[`${option[props.valueKey]}-${SELECT_ITEM_KEY}`];
    if (!entry) return;

    option.selected = entry.selected ? 1 : 0;
    addToValues(option[props.valueKey], entry.selected);
  });
}

// --- Values --------------------------------------------------------------
function addToValues(value, selected) {
  value = value.toString();

  if (!props.multiple) temporaryValues.splice(0);

  const index = temporaryValues.indexOf(value);
  if (selected && index === -1) {
    temporaryValues.push(value);
  } else if (!selected && index !== -1) {
    temporaryValues.splice(index, 1);
  }

  if (request.select) return;
  allSelected.value = flatOptionsList.value.length === temporaryValues.length ? 1 : 0;
}

function selectOptions(list) {
  const values = list || temporaryValues;
  flatOptionsList.value.forEach(option =>
      option.selected = values.indexOf(option[props.valueKey].toString()) !== -1 ? 1 : 0
  );
}

function applyModelValue() {
  let values = (Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue])
      .filter(value => value !== undefined && value !== null && value !== '')
      .map(value => value.toString());

  if (values.length && !props.multiple) values = values.slice(0, 1);

  applyChanges(values, true);
}

function applyChanges(values, unselectOldValue = false) {
  if (!Array.isArray(values)) return;

  if (!request.paginated || unselectOldValue) {
    temporaryValues
        .filter(value => !values.includes(value))
        .forEach(value => addToValues(value, false));
  }

  values.forEach(value => addToValues(value, true));
  selectOptions();
}

// Form data may carry values that don't exist in a static payload — drop them
function clearStaticPayloadValues() {
  const values = temporaryValues.filter(value =>
      flatOptionsList.value.find(opt => opt[props.valueKey].toString() === value.toString())
  );
  applyChanges(values);
  storeValues(true);
}

// --- Commit & emit -------------------------------------------------------
function storeValues(force = false, updateModelVal = true) {
  // Multiple dropdown selects stage changes until applied
  if (!props.inline && props.multiple && !force) return;

  inputValues.splice(0, inputValues.length, ...temporaryValues);

  if (updateModelVal) updateModelValue();
}

// In select mode the committed selection is published to form data so sibling
// components can watch it. This runs only when the selection is settled — on
// apply/close and once the initial preload resolves — never on intermediate
// clicks. The write is idempotent (skips when the serialized selection is
// unchanged) so watchers fire at most once per commit.
let lastFormDataValue;
function syncFormData() {
  if (!request.select || !formData) return;

  const value = formattedSelection.value;
  const serialized = JSON.stringify(value);
  if (serialized === lastFormDataValue) return;

  lastFormDataValue = serialized;
  formData[`${SELECT_KEY}${parsedName.value}`] = value;
}

function apply(values) {
  if (request.select) {
    if (!props.inline) preload();
    syncFormData();
    emitSelect();
    return;
  }

  applyChanges(values);
  storeValues(true);
  emitSelect();

  if (props.autocomplete) {
    unwatch();
    request.search = null;
    setupWatchers();
  }
}

function emitSelect() {
  if (request.select) {
    emit('select', {
      data: formattedSelection.value,        // user-action deltas (sent to backend)
      name: `${SELECT_KEY}${selectFieldName.value}`,
      selection: request.selection,
      values: [...temporaryValues]           // full current selection, incl. server preselection
    });
    return;
  }

  const selectedOptions = inputValues
      .map(value => flatOptionsList.value.find(opt => opt[props.valueKey].toString() === value.toString()))
      .filter(Boolean);

  emit('select', props.multiple
      ? (selectedOptions.length ? selectedOptions : null)
      : (selectedOptions[0] || null));
}

function updateModelValue() {
  emit('update:modelValue', props.multiple
      ? (inputValues.length ? inputValues : null)
      : (inputValues[0] ?? null));
}

// --- Watchers ------------------------------------------------------------
function setupWatchers() {
  if (!watchers.search) {
    watchers.search = watch(() => request.search, () => {
      if (!isStatic.value) return debouncedLoad();
      options.forEach(setOptionVisibility);
    });
  }

  if (!watchers.modelValue) {
    watchers.modelValue = watch(() => props.modelValue, (value) => {
      if (ignoreValueWatcher) return;
      ignoreValueWatcher = true;

      applyModelValue();
      storeValues(true, false);
      nextTick(() => {
        updateModelValue();
        if (request.select && value?.length === 1) {
          addToSelection({ [props.valueKey]: value[0] }, true, SELECT_ITEM_KEY);
        }
        nextTick(() => { ignoreValueWatcher = false; });
      });
    }, { deep: props.multiple, immediate: true });
  }

  if (!watchers.payload) {
    watchers.payload = watch(() => props.payload, (value) => {
      if (typeof props.payload === 'string') {
        freshLoad(true);
        return;
      }
      unwatch();
      options.splice(0);
      addOptions(value);
    }, { deep: typeof props.payload === 'string' });
  }

  if (!watchers.perPage) {
    watchers.perPage = watch(() => request.perPage, (next, prev) => {
      if (next != prev) freshLoad(true);
    });
  }
}

// Registered once on init (not inside setupWatchers, which is skipped on the
// preload-only path) and kept out of unwatch() teardown so it lives as long as
// the component does.
function setupDependencyWatcher() {
  if (!props.filters || watchers.filters) return;

  watchers.filters = watch(() => props.filters, () => {
    preloadedOptions.splice(0);

    preloading.value = true;
    freshLoad().then(() => {
      pruneSelectionToLoaded();
      preloading.value = false;
    });
  }, { deep: true });
}

function pruneSelectionToLoaded() {
  const available = flatOptionsList.value.map(option => option[props.valueKey].toString());

  const removed = temporaryValues.filter(value => !available.includes(value.toString()));
  if (!removed.length) return;

  removed.forEach(value => addToValues(value, false));
  selectOptions();
  storeValues(true);
}

function unwatch(leavePayloadWatcher = false) {
  Object.keys(watchers).forEach((key) => {
    if (key === 'filters') return;
    if (key === 'payload' && leavePayloadWatcher) return;
    watchers[key]();
    delete watchers[key];
  });
}

function debouncedLoad() {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(freshLoad, 300);
}
</script>

<style lang="scss" scoped>
@import "@/css/functions";
@import "@/css/variables";

.select {
  width: 100%;
  display: flex;
  flex-direction: column;

  &:deep(> .input-group) {
    flex-wrap: nowrap;
  }

  &:not(.select--inline) {
    &.open input {
      padding-left: 0;
      outline: 0;
    }
  }

  :deep(.select__arrow) {
    font-size: toRem(10) !important;
  }

  :deep(.select__search) {
    padding-left: 0;
  }

  &--sm {
    [class^="icon-chevron-"],
    .icon-search {
      font-size: toRem(10);
    }

    .form-control-sm + .input-group-text {
      padding-left: 0;
    }
  }
}
</style>
